import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Alert,
  Typography,
  Grid,
  useTheme,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { isAfter } from "date-fns";
import { isEqual } from "lodash";
import React, { useState, useCallback, useMemo, memo } from "react";
import { Trans, useTranslation } from "react-i18next";
import CustomConfirmationChangesDialog from "src/containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { CustomDate } from "src/models/custom-date/custom-date";
import { TnAEmployeesAnomaliesFilter } from "src/models/tna-employee-anomalies";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { EmployeeInfoBox, FieldsContainer, StyledTable } from "./styles";
import useAuthorization from "../../../../../custom-hooks/useAuthorization/useAuthorization";
import {
  TnATimesheetConfirmationsDTO,
  TnATimesheetConfirmationSTO,
} from "../../../../../models/tna-timesheet-confirmation";
import { EmployeeAnomaliesQueryApiService } from "../../../../../services/employee-anomalies-query/employee-anomalies-query-api.service";
import { TnATimesheetConfirmationsApiService } from "../../../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { AUTHORIZATIONS } from "../../../../../utils/constants/authorization-constants";
import { toastifyResponse } from "../../../../../utils/toastify-response/toastify-response";
import ConfirmationChangesDialog from "../../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../DialogActions/DialogActions";
import DialogTitle from "../../../../DialogTitle/DialogTitle";

import LoadingOverlay from "../../../../LoadingOverlay/LoadingOverlay";
import { TnATimesheetConfirmationsRow } from "../SendMassSignoffRequests";

const SignoffConfirmationRequests = ({
  defaultLegalEntity = null,
  handleNotifyParent,
  selectedItems,
  onClose,
  isOpen,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_time_sheet_confirmation"; // tRP - translation root path

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const DEFAULT_ITEM: Partial<TnATimesheetConfirmationsDTO> = useMemo(() => {
    return {
      legalEntity: defaultLegalEntity ?? null,
      asOfDate: CustomDate.FromDate(new Date()),
    };
  }, [defaultLegalEntity]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      loading: false,
      signOffRequest: DEFAULT_ITEM as TnATimesheetConfirmationsDTO,
      isCCDOpen: false,
      areFieldsValidated: false,
      isWarningCCCDOpen: false,
      timesheetAnomalies: 0,
    };
  }, [DEFAULT_ITEM]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const { itemsForRequest, itemsForReminder } = useMemo(() => {
    const requests: TnATimesheetConfirmationsRow[] = [];
    const reminders: TnATimesheetConfirmationsRow[] = [];

    selectedItems.forEach((item) => {
      if (!item.lastSignoffRequest) {
        requests.push(item);
      } else {
        reminders.push(item);
      }
    });

    return { itemsForRequest: requests, itemsForReminder: reminders };
  }, [selectedItems]);

  // #region change field
  const handleChangeField = useCallback(
    (
      field: keyof TnATimesheetConfirmationsDTO,
      value: TnATimesheetConfirmationsDTO[keyof TnATimesheetConfirmationsDTO]
    ) => {
      setState((state) => ({
        ...state,
        signOffRequest: {
          ...state.signOffRequest,
          [field]: value,
        },
      }));
    },
    []
  );
  // #endregion

  // #region handle save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, error: "", loading: true }));

    try {
      const responses = [];
      const validItemsForRequest = itemsForRequest.filter((_item) => !_item.hasNoAccess);

      const valiItemsForReminder = itemsForReminder.filter((_item) => !_item.hasNoAccess);

      if (validItemsForRequest.length > 0) {
        const requestItems: TnATimesheetConfirmationsDTO[] = validItemsForRequest.map((selectedItem) => ({
          ...selectedItem,
          id: uuidv4(),
          asOfDate: state.signOffRequest.asOfDate,
        }));

        const requestResponse = await new TnATimesheetConfirmationsApiService().saveItems(requestItems);
        responses.push(requestResponse);

        if (requestResponse.error) {
          setState((state) => ({
            ...state,
            error: requestResponse.error,
            loading: false,
          }));
          toastifyResponse(requestResponse, "failed");
          return;
        }
      }

      if (valiItemsForReminder.length > 0) {
        const reminderItems = valiItemsForReminder.map(
          (item) =>
            ({
              id: uuidv4(),
              isDeleted: false,
              employee: item.employee,
            } as TnATimesheetConfirmationSTO)
        );

        const apiService = new TnATimesheetConfirmationsApiService();
        apiService.useSaveItemsPath("/SendSignOffReminderNotification");
        const reminderResponse = await apiService.saveItems(reminderItems);
        responses.push(reminderResponse);

        if (reminderResponse.error) {
          setState((state) => ({
            ...state,
            error: reminderResponse.error,
            loading: false,
          }));
          toastifyResponse(reminderResponse, "failed");
          return;
        }
      }

      setState({ ...INITIAL_STATE });
      handleNotifyParent();
      if (onClose) {
        onClose();
      }

      if (responses.length > 0) {
        toastifyResponse(responses[0]);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, handleNotifyParent, state.signOffRequest, itemsForRequest, itemsForReminder, onClose]);
  // #endregion

  // #region check timesheet anomalies and save
  const checkTimesheetAnomaliesAndSave = useCallback(async () => {
    setState((state) => ({ ...state, error: "", loading: true }));

    if (itemsForRequest.length === 0) {
      await handleSave();
      return;
    }

    const tnAEmployeeAnomalies = itemsForRequest.map((item) => ({
      employeeID: item.employee?.id,
      fromDate: item.fromDate
        ? `${item.fromDate?.year}-${item.fromDate?.month < 10 ? `0${item.fromDate?.month}` : item.fromDate?.month}-${
            item.fromDate?.day < 10 ? `0${item.fromDate?.day}` : item.fromDate?.day
          }`
        : null,
    }));

    try {
      const anomalyCheckResponse = await new EmployeeAnomaliesQueryApiService().loadItems({
        tnAEmployeeAnomalies,
        asOfDate: state.signOffRequest?.asOfDate
          ? `${state.signOffRequest?.asOfDate?.year}-${
              state.signOffRequest?.asOfDate?.month < 10
                ? `0${state.signOffRequest?.asOfDate?.month}`
                : state.signOffRequest?.asOfDate?.month
            }-${
              state.signOffRequest?.asOfDate?.day < 10
                ? `0${state.signOffRequest?.asOfDate?.day}`
                : state.signOffRequest?.asOfDate?.day
            }`
          : null,
      } as TnAEmployeesAnomaliesFilter);

      if (anomalyCheckResponse.error) {
        setState((state) => ({
          ...state,
          error: anomalyCheckResponse.error,
          loading: false,
        }));
        return;
      }

      if (anomalyCheckResponse.items[0].anomalies > 0) {
        setState((state) => ({
          ...state,
          isWarningCCCDOpen: true,
          timesheetAnomalies: anomalyCheckResponse.items[0].anomalies,
          loading: false,
        }));
      } else {
        await handleSave();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
        loading: false,
      }));
    }
  }, [itemsForRequest, state.signOffRequest?.asOfDate, handleSave]);
  // #endregion

  // #region handle discard changes
  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
    if (onClose) {
      onClose();
    }
  }, [INITIAL_STATE, onClose]);
  // #endregion

  // #region reset and close dialog
  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.signOffRequest, INITIAL_STATE.signOffRequest)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };
  // #endregion

  // #region are fields validated
  const areFieldsValidated = useCallback((): boolean => {
    if (itemsForRequest.length === 0 && itemsForReminder.length > 0) {
      return hasAccess;
    }

    return (
      hasAccess &&
      !!state.signOffRequest?.asOfDate &&
      !isAfter(CustomDate.ToDate(state.signOffRequest?.asOfDate) as Date, new Date()) &&
      !!selectedItems.find((_selectedItem) => !_selectedItem.hasNoAccess)
    );
  }, [selectedItems, state.signOffRequest?.asOfDate, hasAccess, itemsForRequest.length, itemsForReminder.length]);
  // #endregion

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={t(`${tRP}.title_for_mass_signoff_confirmation`, { count: selectedItems.length })}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              {itemsForRequest.length > 0 && (
                <Grid
                  item
                  xs={itemsForReminder.length === 0 ? 12 : 6}
                  sx={{ flexDirection: "column", gap: "12px" }}
                >
                  <Box sx={{ marginTop: 1, marginBottom: 0 }}>
                    <DesktopDatePicker
                      inputFormat="dd.MM.yyyy"
                      mask="__.__.____"
                      label={`${t(`${tRP}.fields.asOf_date_r`)}`}
                      value={CustomDate.ToDate(state.signOffRequest?.asOfDate)}
                      onChange={(newValue) => {
                        handleChangeField("asOfDate", newValue ? CustomDate.FromDate(newValue) : null);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                      disableFuture
                    />
                  </Box>
                </Grid>
              )}
              <EmployeeInfoBox>
                <Typography
                  // variant="body1"
                  fontWeight={"bold"}
                >
                  {t(`${tRP}.you_have_selected`)}
                </Typography>
                <Grid style={{ display: "flex", gap: "10px" }}>
                  {itemsForRequest.length > 0 && (
                    <Grid style={{ width: itemsForReminder.length === 0 ? "100%" : "calc(50% - 5px)" }}>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">
                              {t(`${tRP}.employees_to_request_signoff`, { count: itemsForRequest.length })}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itemsForRequest
                            .sort((a, b) => {
                              return a.employee?.name.localeCompare(b.employee?.name ?? "") ?? 0;
                            })
                            .map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  {item.employee?.name} &nbsp;
                                  {item.hasNoAccess && (
                                    <span style={{ color: theme.palette.warning.main, fontSize: "10px" }}>
                                      ({t(`${tRP}.not_linked_to_user`)})
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </StyledTable>
                    </Grid>
                  )}

                  {itemsForReminder.length > 0 && (
                    <>
                      <Grid style={{ width: itemsForRequest.length === 0 ? "100%" : "calc(50% - 5px)" }}>
                        <StyledTable>
                          <TableHead>
                            <TableRow>
                              <TableCell align="center">
                                {t(`${tRP}.employees_to_remind`, { count: itemsForReminder.length })}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {itemsForReminder
                              .sort((a, b) => {
                                return a.employee?.name.localeCompare(b.employee?.name ?? "") ?? 0;
                              })
                              .map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    {item.employee?.name} &nbsp;
                                    {item.hasNoAccess && (
                                      <span style={{ color: theme.palette.warning.main, fontSize: "10px" }}>
                                        ({t(`${tRP}.not_linked_to_user`)})
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </StyledTable>
                      </Grid>
                    </>
                  )}
                </Grid>
              </EmployeeInfoBox>
            </FieldsContainer>
            {state.error && <Alert severity="error">{state.error}</Alert>}
          </DialogContent>
          <DialogActions
            handleSave={checkTimesheetAnomaliesAndSave}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
            customSaveName={
              itemsForRequest.length > 0 && itemsForReminder.length > 0
                ? t(`${tRP}.send_requests_and_reminders`)
                : itemsForRequest.length > 0
                ? t(`${tRP}.send_requests`)
                : t(`${tRP}.send_reminders`)
            }
          >
            {!!selectedItems.find((_selectedItem) => _selectedItem.hasNoAccess) && (
              <Alert severity="warning">{t(`${tRP}.not_linked_to_user_hint`)}</Alert>
            )}
          </DialogActions>
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />

      <CustomConfirmationChangesDialog
        open={state.isWarningCCCDOpen}
        loading={state.loading}
        title={`${t(`${tRP}.timesheet_has_anomalies`)}`}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            isWarningCCCDOpen: false,
            timesheetAnomalies: 0,
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              handleSave();
            },
          },
        ]}
      >
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography>
            <Trans
              i18nKey={`${tRP}.mass_request_confimation_message`}
              values={{
                count: selectedItems.length,
                timesheetAnomalies: state.timesheetAnomalies,
              }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Grid>
      </CustomConfirmationChangesDialog>
    </>
  );
};
export default memo(SignoffConfirmationRequests);
